import { cs } from '../../lib/currency';
import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { CreditCard, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

/**
 * PostManualCreditDialog (iter239) — admin/staff posts an account credit to
 * a parent or customer.
 *
 *   • Notes (reason) are MANDATORY.
 *   • The currently-logged-in user is captured server-side via the auth
 *     dependency — frontend does not need to send anything extra.
 *   • Backend sends the "Manual Credit Posted" email automatically on
 *     positive credit. The dialog surfaces a success toast with whether
 *     the email was sent.
 *
 * Props:
 *   userId         — parent/customer user id (string, required)
 *   userName       — display name for the dialog title
 *   userRole       — 'parent' | 'customer' (cosmetic; doesn't affect API)
 *   onPosted       — callback after a successful POST. Use this to refresh
 *                    credit balance / credit history on the calling page.
 *   trigger        — optional custom trigger element. Falls back to a
 *                    pill-styled "Post Credit" button.
 */
export default function PostManualCreditDialog({
  userId,
  userName,
  userRole = 'parent',
  onPosted,
  trigger,
}) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setAmount('');
    setNotes('');
  };

  const handleClose = (next) => {
    if (submitting) return;
    setOpen(next);
    if (!next) reset();
  };

  const handleSubmit = async () => {
    const amt = parseFloat(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error('Enter a positive credit amount');
      return;
    }
    const trimmedNotes = (notes || '').trim();
    if (!trimmedNotes) {
      toast.error('Notes are required — explain why the credit is being posted');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/admin/customers/${userId}/credit/adjust`, {
        amount: amt,
        reason: trimmedNotes,
      });
      toast.success(
        `Posted ${cs()}${amt.toFixed(2)} credit.${data?.email_sent ? ' Notification email sent.' : ''}`,
        { duration: 5000 }
      );
      setOpen(false);
      reset();
      onPosted?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to post credit');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      {trigger ? (
        <span onClick={() => setOpen(true)}>{trigger}</span>
      ) : (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setOpen(true)}
          className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
          data-testid="post-manual-credit-btn"
        >
          <Plus className="h-4 w-4 mr-1" />
          Post Credit
        </Button>
      )}
      <DialogContent data-testid="post-manual-credit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-800">
            <CreditCard className="h-5 w-5" />
            Post Manual Credit
          </DialogTitle>
          <DialogDescription>
            For {userName} ({userRole})
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <Label htmlFor="manual-credit-amount">
              Credit amount ($) <span className="text-red-500">*</span>
            </Label>
            <Input
              id="manual-credit-amount"
              type="number"
              min="0.01"
              step="0.01"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="manual-credit-amount-input"
              disabled={submitting}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="manual-credit-notes">
              Notes <span className="text-red-500">*</span>
              <span className="ml-1 text-xs text-slate-500">— shown to the customer in the email</span>
            </Label>
            <Textarea
              id="manual-credit-notes"
              rows={4}
              placeholder="e.g. Goodwill credit for the canceled session on May 14. We're sorry for the inconvenience."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="manual-credit-notes-input"
              disabled={submitting}
              required
            />
          </div>
          <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-2">
            <p className="font-medium text-slate-700 mb-1">What happens next?</p>
            <ul className="list-disc list-inside space-y-0.5">
              <li>The credit is added to the customer's account balance immediately.</li>
              <li>A notification email is sent to the customer with your notes.</li>
              <li>The action is logged in the credit history with your name.</li>
            </ul>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleClose(false)}
            disabled={submitting}
            data-testid="cancel-post-credit-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-emerald-700 hover:bg-emerald-800"
            data-testid="confirm-post-credit-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-1 animate-spin" /> Posting…
              </>
            ) : (
              'Post Credit'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
